import { classifyPositionEvent } from './rollClassification.js';

export type DomainEventType = 'opened' | 'rolled' | 'closed' | 'expired' | 'assigned' | 'exercised';

export interface EventLegSlice {
  side: 'short' | 'long';
  optionType: 'put' | 'call';
  strike: number;
  expiration: string;       // YYYY-MM-DD (UTC)
  quantity: number;
  pricePerContract: number;
  dollarAmount: number;     // signed at open: credit positive, debit negative
}

export interface OpenedPayload { strategyLabel: string; legs: EventLegSlice[]; netCredit: number }
export interface RolledPayload {
  closedLegs: EventLegSlice[]; openedLegs: EventLegSlice[];
  closeCost: number; openCredit: number; netRoll: number;
  oldExpiration: string; newExpiration: string;
}
export interface ClosedPayload { legs: EventLegSlice[]; netCloseCost: number }
export interface ExpiredPayload { legs: EventLegSlice[]; premiumKept: number }
export interface AssignedPayload { legs: EventLegSlice[]; notional: number }

export type PositionEventPayload =
  | OpenedPayload | RolledPayload | ClosedPayload | ExpiredPayload | AssignedPayload;

export interface PositionEventFact {
  chainId: string;
  positionId: string | null;
  symbol: string;
  eventType: DomainEventType;
  occurredAt: Date;
  payload: PositionEventPayload;
  fingerprint: string; // readable preimage, unique per (user, event); see buildFingerprint
}

export interface DeriveLegTxn { transactionType: string; quantity: number; pricePerContract: number; executedAt: Date }
export interface DeriveLeg {
  action: string; // opening action: STO/BTO (STC/BTC treated as their opener's side)
  optionType: 'put' | 'call';
  strikePrice: number;
  expirationDate: Date;
  quantity: number;
  quantityRemaining: number;
  pricePerContract: number;
  closeReason: string | null;
  transactions: DeriveLegTxn[];
}
export interface DerivePosition {
  id: string;
  fingerprint: string | null;
  rollNumber: number;
  openedAt: Date;
  closedAt: Date | null;
  closeReason: string | null;
  netPremium: number;
  status: string;
  legs: DeriveLeg[];
}
export interface DeriveChain { chainId: string; symbol: string; userId: string; positions: DerivePosition[] }

export const utcDateKey = (d: Date): string => d.toISOString().slice(0, 10);

const isShort = (l: DeriveLeg) => l.action === 'STO' || l.action === 'STC';

const toSlice = (l: DeriveLeg): EventLegSlice => ({
  side: isShort(l) ? 'short' : 'long',
  optionType: l.optionType,
  strike: l.strikePrice,
  expiration: utcDateKey(l.expirationDate),
  quantity: l.quantity,
  pricePerContract: l.pricePerContract,
  dollarAmount: l.pricePerContract * l.quantity * 100 * (isShort(l) ? 1 : -1),
});

/** Direction-aware close value: short buybacks are paid, long sales are received. */
const closeValue = (l: DeriveLeg): number => {
  const v = l.transactions
    .filter((t) => t.transactionType === 'close')
    .reduce((s, t) => s + t.pricePerContract * t.quantity * 100, 0);
  return isShort(l) ? v : -v;
};

/**
 * A leg as it was CLOSED — the counterpart to `toSlice`, which describes it as it was OPENED.
 *
 * `closedLegs` on a `rolled` payload and `legs` on a `closed` payload used `toSlice`, so a leg
 * bought back at 11.58 was recorded at the 2.69 it was sold for. Nothing printed those numbers
 * yet, which is why it went unnoticed; the AI position context and any per-leg display would.
 *
 * `pricePerContract` is weighted by fill quantity, not fill count (same rule as
 * `consolidateLegSlices` below): two lots of 6 @0.06 and 4 @0.09 average to 0.072, not 0.075.
 * A single fill is taken verbatim rather than through the division, so the common case carries
 * the exact price the broker reported instead of a float-noise neighbour of it.
 *
 * `quantity` is the closed quantity — for a fully closed leg that equals `l.quantity`, which is
 * why event fingerprints (built from `toSlice`'s open slices, see `deriveChainEvents`) do not
 * move.
 *
 * `dollarAmount` is CASH: received positive, paid negative. A short bought back is negative, a
 * long sold is positive — exactly `−closeValue(l)`, since `closeValue` is the cost TO close
 * (positive = money out the door).
 */
const closeSlice = (l: DeriveLeg): EventLegSlice => {
  const closes = l.transactions.filter((t) => t.transactionType === 'close');
  const quantity = closes.reduce((s, t) => s + t.quantity, 0);
  const pricePerContract = closes.length === 1
    ? closes[0].pricePerContract
    : quantity === 0
      ? 0
      : closes.reduce((s, t) => s + t.pricePerContract * t.quantity, 0) / quantity;
  return {
    side: isShort(l) ? 'short' : 'long',
    optionType: l.optionType,
    strike: l.strikePrice,
    expiration: utcDateKey(l.expirationDate),
    quantity,
    pricePerContract,
    dollarAmount: -closeValue(l),
  };
};

const lastCloseTime = (legs: DeriveLeg[]): Date | null => {
  const times = legs.flatMap((l) => l.transactions.filter((t) => t.transactionType === 'close').map((t) => t.executedAt.getTime()));
  return times.length ? new Date(Math.max(...times)) : null;
};

const isActivelyClosed = (l: DeriveLeg): boolean =>
  l.quantityRemaining === 0 &&
  (l.closeReason === 'closed' ||
    (l.closeReason == null && l.transactions.some((t) => t.transactionType === 'close')));

/**
 * Which legs a `rolled` event actually closed.
 *
 * `classifyPositionEvent` decides WHETHER a position is a roll; this decides WHICH legs that roll
 * closed, and the two are deliberately separate. The pairing used to be `sorted[i - 1]` — the
 * previous position in time — which is right for a chain that rolls as a whole and wrong for an
 * iron condor rolled one side at a time. That shape is a chain of positions (iron condor -> put
 * spread -> call spread -> put spread -> call spread), so the previous position in time is the
 * OTHER SIDE: measured on production 2026-09-16, one roll carried the other side's 40-dollar
 * close instead of its own 2,180, and the next carried no closed legs at all.
 * `positions.parent_position_id` is the previous position in time too, so it cannot pair sides.
 *
 * The rule, in order:
 *  1. Every actively closed leg of an EARLIER position whose last close fill lands on the same UTC
 *     day the new position opened, and that no earlier roll in this chain has already claimed.
 *     Same-day is what ties a roll's two halves together — a roll closes and reopens in one order.
 *  2. Of those, the ones whose `optionType` appears among the new legs: a put roll claims puts.
 *     If that leaves nothing, take all the same-day candidates — a roll that flipped sides closed
 *     calls to open puts, and dropping the filter is the only way it sees its own close.
 *  3. If there is no same-day candidate at all, the previous position's unclaimed actively closed
 *     legs, exactly as before. This is what keeps every other chain shape byte-identical.
 *
 * `claimed` is threaded across the chain's rolls so no leg is counted twice: without it, two rolls
 * of the same option type on one day would each take the same close and double its cost.
 *
 * Pure — it reads no clock and mutates nothing, `claimed` included.
 */
export function pickRolledLegs(
  earlier: DerivePosition[],
  pos: DerivePosition,
  claimed: Set<DeriveLeg>,
): DeriveLeg[] {
  const eligible = (l: DeriveLeg) => !claimed.has(l) && isActivelyClosed(l);

  const openedDay = utcDateKey(pos.openedAt);
  const closedSameDay = (l: DeriveLeg) => {
    const last = lastCloseTime([l]);
    return last != null && utcDateKey(last) === openedDay;
  };

  const sameDay = earlier.flatMap((p) => p.legs).filter((l) => eligible(l) && closedSameDay(l));
  if (sameDay.length > 0) {
    const newTypes = new Set(pos.legs.map((l) => l.optionType));
    const ownType = sameDay.filter((l) => newTypes.has(l.optionType));
    return ownType.length > 0 ? ownType : sameDay;
  }

  const prior = earlier.length > 0 ? earlier[earlier.length - 1] : null;
  return prior ? prior.legs.filter(eligible) : [];
}

/**
 * Collapses same-contract fill lots into one entry per (optionType, strike, expiration, side)
 * group, summing quantity and dollarAmount, before strategy detection runs (Task 21c) — the
 * journal-narrative sibling of `consolidateLegsForDetection` in
 * `packages/api/src/services/calculations/options.ts`.
 *
 * The two helpers are NOT shared, by design: `EventLegSlice` has already reduced BTO/STO/BTC/STC
 * down to a `side` and carries no underlying symbol at all (a `DeriveChain` is already scoped to
 * one symbol) — a different shape from `NormalizedLeg`'s, so a single generic helper would have
 * to either invent fields neither caller has or lose type fidelity on one side. `shared` cannot
 * import from `api` (dependency runs the other way — `api` depends on `@steward/shared`), so this
 * small duplicate lives here rather than being hoisted.
 *
 * `pricePerContract` is weighted by fill quantity, not fill count, matching `consolidateLegs`
 * (web) and `consolidateLegsForDetection` (api): a 1-lot fill and a 9-lot fill at different
 * prices must not average as if they were equal-sized. `dollarAmount` is already quantity-scaled,
 * so it just sums.
 */
function consolidateLegSlices(legs: EventLegSlice[]): EventLegSlice[] {
  const groups = new Map<string, EventLegSlice[]>();

  for (const leg of legs) {
    const key = `${leg.optionType}|${leg.strike}|${leg.expiration}|${leg.side}`;
    const group = groups.get(key);
    if (group) {
      group.push(leg);
    } else {
      groups.set(key, [leg]);
    }
  }

  const result: EventLegSlice[] = [];
  for (const group of groups.values()) {
    if (group.length === 1) {
      result.push(group[0]);
      continue;
    }
    const quantity = group.reduce((s, l) => s + l.quantity, 0);
    const dollarAmount = group.reduce((s, l) => s + l.dollarAmount, 0);
    const pricePerContract = quantity === 0
      ? 0
      : group.reduce((s, l) => s + l.pricePerContract * l.quantity, 0) / quantity;
    result.push({ ...group[0], quantity, dollarAmount, pricePerContract });
  }

  return result;
}

export function detectStrategy(legs: EventLegSlice[]): string {
  const puts = legs.filter((l) => l.optionType === 'put');
  const calls = legs.filter((l) => l.optionType === 'call');
  const shorts = legs.filter((l) => l.side === 'short');
  if (legs.length === 4 && puts.length === 2 && calls.length === 2 && shorts.length === 2) return 'Iron Condor';
  if (legs.length === 2 && puts.length === 2 && shorts.length === 1) return 'Put Credit Spread';
  if (legs.length === 2 && calls.length === 2 && shorts.length === 1) return 'Call Credit Spread';
  if (legs.length === 1 && puts.length === 1 && shorts.length === 1) return 'Short Put';
  if (legs.length === 1 && calls.length === 1 && shorts.length === 1) return 'Short Call';
  return `${legs.length}-Leg Position`;
}

const legKeys = (legs: EventLegSlice[]): string =>
  legs.map((l) => `${l.optionType}:${l.strike}:${l.expiration}:${l.side}:${l.quantity}`).sort().join(',');

const economicChainKey = (chain: DeriveChain, first: DerivePosition): string =>
  first.fingerprint ?? `${chain.symbol}:${utcDateKey(first.openedAt)}:${legKeys(first.legs.map(toSlice))}`;

const sortPositions = (positions: DerivePosition[]): DerivePosition[] =>
  [...positions].sort(
    (a, b) => a.openedAt.getTime() - b.openedAt.getTime() || a.rollNumber - b.rollNumber || a.id.localeCompare(b.id),
  );

/**
 * The prefix every event fingerprint of this chain starts with, or null for a chain with no
 * positions. It carries the ECONOMIC chain key, not the chain id, so it survives a replay that
 * regenerates ids — which is what lets capture find a chain's stored events that no longer
 * derive (a stale event is a row with this prefix whose full fingerprint the derivation no
 * longer produces). Compare with `substr(fingerprint, 1, length(prefix)) = prefix`, never LIKE:
 * a position fingerprint can carry `_`, which LIKE would read as a wildcard.
 */
export function chainEventPrefix(chain: DeriveChain): string | null {
  const sorted = sortPositions(chain.positions);
  if (sorted.length === 0) return null;
  return `v1|${chain.userId}|${economicChainKey(chain, sorted[0])}|`;
}

function buildFingerprint(userId: string, chainKey: string, eventType: DomainEventType, occurredAt: Date, legs: EventLegSlice[]): string {
  return `v1|${userId}|${chainKey}|${eventType}|${utcDateKey(occurredAt)}|${legKeys(legs)}`;
}

export function deriveChainEvents(chain: DeriveChain): PositionEventFact[] {
  const events: PositionEventFact[] = [];
  const sorted = sortPositions(chain.positions);
  if (sorted.length === 0) return events;
  const chainKey = economicChainKey(chain, sorted[0]);

  const push = (pos: DerivePosition, eventType: DomainEventType, occurredAt: Date, payload: PositionEventPayload, fpLegs: EventLegSlice[]) =>
    events.push({
      chainId: chain.chainId, positionId: pos.id, symbol: chain.symbol,
      eventType, occurredAt, payload,
      fingerprint: buildFingerprint(chain.userId, chainKey, eventType, occurredAt, fpLegs),
    });

  // Legs already claimed by an earlier roll in this chain, so no close is counted twice.
  const claimed = new Set<DeriveLeg>();

  sorted.forEach((pos, i) => {
    const prior = i > 0 ? sorted[i - 1] : null;
    const slices = pos.legs.map(toSlice);
    if (classifyPositionEvent(pos, prior) === 'opened') {
      push(pos, 'opened', pos.openedAt, {
        // Consolidate fill lots before detection only (Task 21c) — an iron condor whose legs each
        // arrived as several partial fills must be detected as 4 legs, not 8+. `legs` in the
        // payload stays the raw per-fill slices; only the detector's input is consolidated.
        strategyLabel: detectStrategy(consolidateLegSlices(slices)), legs: slices, netCredit: pos.netPremium,
      }, slices);
    } else {
      // The legs this roll actually closed — same day, same side, unclaimed (`pickRolledLegs`),
      // NOT simply the previous position in time.
      const rolledLegs = pickRolledLegs(sorted.slice(0, i), pos, claimed);
      for (const l of rolledLegs) claimed.add(l);
      // Closed legs are described at their CLOSE price (see `closeSlice`). The fingerprint
      // below keys off `slices` — the NEW position's opening legs — so this is invisible to it.
      const closedSlices = rolledLegs.map(closeSlice);
      const closeCost = rolledLegs.reduce((s, l) => s + closeValue(l), 0);
      push(pos, 'rolled', pos.openedAt, {
        closedLegs: closedSlices, openedLegs: slices,
        closeCost, openCredit: pos.netPremium, netRoll: pos.netPremium - closeCost,
        oldExpiration: closedSlices.map((l) => l.expiration).sort().at(-1) ?? '',
        newExpiration: slices.map((l) => l.expiration).sort().at(-1) ?? '',
      }, slices);
    }
  });

  sorted.forEach((pos) => {
    const expiredLegs = pos.legs.filter((l) => l.closeReason === 'expired');
    if (expiredLegs.length > 0) {
      const slices = expiredLegs.map(toSlice);
      const occurredAt = new Date(Math.max(...expiredLegs.map((l) => l.expirationDate.getTime())));
      push(pos, 'expired', occurredAt, {
        legs: slices, premiumKept: slices.reduce((s, l) => s + l.dollarAmount, 0),
      }, slices);
    }

    for (const reason of ['assigned', 'exercised'] as const) {
      const affected = pos.legs.filter((l) => l.closeReason === reason);
      if (affected.length > 0) {
        const slices = affected.map(toSlice);
        const occurredAt = lastCloseTime(affected) ?? pos.closedAt ?? pos.openedAt;
        push(pos, reason, occurredAt, {
          legs: slices, notional: affected.reduce((s, l) => s + l.strikePrice * l.quantity * 100, 0),
        }, slices);
      }
    }

    // One `closed` event per DAY of actively closed legs a roll did not claim (2026-09-17).
    //
    // This used to fire only once every leg of the position was flat, and never when the next
    // position was a roll. Closing one side of an iron condor outright — the put side on the
    // 9th, calls left to expire — therefore produced no event at all until the calls resolved,
    // and then one back-dated to the 9th: the month in progress understated its costs, and a
    // finished month could change weeks later (measured against Schwab's ledger on production,
    // 2026-09-17). A side closed outright before a roll of the other side was skipped for good.
    //
    // `claimed` already says precisely which closes belong to a roll, so the successor gate is
    // redundant with it, and grouping by close day is what keeps a position whose legs all
    // closed together — 214 of 224 on production — emitting exactly the event it did before,
    // byte-identical fingerprint included. The payload describes the legs at their CLOSE price;
    // the fingerprint stays on the OPEN slices, so an event captured mid-fill is corrected in
    // place rather than re-captured once the last lot lands (see `closeSlice`).
    const unclaimedClosed = pos.legs.filter((l) => isActivelyClosed(l) && !claimed.has(l));
    const byDay = new Map<string, DeriveLeg[]>();
    for (const l of unclaimedClosed) {
      const at = lastCloseTime([l]) ?? pos.closedAt ?? pos.openedAt;
      const day = utcDateKey(at);
      byDay.set(day, [...(byDay.get(day) ?? []), l]);
    }
    for (const group of byDay.values()) {
      push(pos, 'closed', lastCloseTime(group) ?? pos.closedAt ?? pos.openedAt, {
        legs: group.map(closeSlice), netCloseCost: group.reduce((s, l) => s + closeValue(l), 0),
      }, group.map(toSlice));
    }
  });

  return events.sort((a, b) => a.occurredAt.getTime() - b.occurredAt.getTime());
}
